import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  decimalToString,
  parseDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import {
  type LongMultiplication,
  multiplyByColumns,
  placePoint,
  roundToOneSigFig,
} from "@/lib/tool-kit/arithmetic/long-multiplication";

/**
 * What the long-multiplication widget shows: the computed multiplication, an estimate to
 * check it against, and the narration of each row. Sentence builders take the widget's
 * own strings so a translation reaches them.
 */
export interface MultiplicationView {
  multiplication: LongMultiplication;
  /** Both factors rounded to one significant figure, and their product. */
  estimate: { a: string; b: string; product: string };
}

export type MultiplicationComputation =
  | { kind: "empty" | "invalid" | "too-long" }
  | { kind: "ok"; view: MultiplicationView };

export interface MultiplicationWords {
  ignorePoints: string;
  rowHeading: string;
  placeNames: readonly string[];
  placeFallback: string;
  placeholderZeros: string;
  placeholderBlank: string;
  zerosCount: string;
  shiftPlaces: string;
  mulLine: string;
  mulLineCarry: string;
  writeCarry: string;
  writeOnly: string;
  writeAll: string;
  rowResult: string;
  skipZero: string;
  onlyRow: string;
  addRows: string;
  pointRule: string;
  zeroProduct: string;
  signRule: string;
}

export interface WorkingSection {
  heading: string;
  note: string | null;
  lines: string[];
}

export function computeMultiplication(
  rawA: string,
  rawB: string
): MultiplicationComputation {
  if (rawA.trim() === "" || rawB.trim() === "") {
    return { kind: "empty" };
  }
  const a = parseDecimal(rawA);
  const b = parseDecimal(rawB);
  if (a === null || b === null) {
    return { kind: "invalid" };
  }
  const multiplication = multiplyByColumns(a, b);
  if ("error" in multiplication) {
    return { kind: "too-long" };
  }
  const roundedA = roundToOneSigFig(a);
  const roundedB = roundToOneSigFig(b);
  const estimate = multiplyByColumns(roundedA, roundedB);
  if ("error" in estimate) {
    return { kind: "too-long" };
  }
  return {
    kind: "ok",
    view: {
      multiplication,
      estimate: {
        a: decimalToString(roundedA),
        b: decimalToString(roundedB),
        product: estimate.productTrimmed,
      },
    },
  };
}

function placeName(position: number, words: MultiplicationWords): string {
  return (
    words.placeNames[position] ?? t(words.placeFallback, { power: position })
  );
}

function digitLine(
  step: LongMultiplication["partials"][number]["steps"][number],
  multiplierDigit: number,
  words: MultiplicationWords
): string {
  const values = {
    digit: step.digit,
    m: multiplierDigit,
    carryIn: step.carryIn,
    product: step.product,
    write: step.written,
    carry: step.carryOut,
  };
  const calc = t(step.carryIn > 0 ? words.mulLineCarry : words.mulLine, values);
  let ending = t(words.writeOnly, values);
  if (step.last && step.product >= 10) {
    ending = t(words.writeAll, values);
  } else if (step.carryOut > 0) {
    ending = t(words.writeCarry, values);
  }
  return `${calc}: ${ending}`;
}

/** Heading, optional place-holder note, and the digit-by-digit lines for each row. */
export function workingSections(
  m: LongMultiplication,
  words: MultiplicationWords,
  locale: string,
  placeholder: "zeros" | "blank"
): WorkingSection[] {
  return m.partials.map((partial, index) => {
    const place = placeName(partial.position, words);
    const heading = t(words.rowHeading, {
      n: index + 1,
      multiplicand: m.multiplicand,
      digit: partial.multiplierDigit,
      place,
    });
    let note: string | null = null;
    if (partial.position > 0) {
      const count = tPlural(
        placeholder === "zeros" ? words.zerosCount : words.shiftPlaces,
        locale,
        partial.position
      ).trim();
      note = t(
        placeholder === "zeros"
          ? words.placeholderZeros
          : words.placeholderBlank,
        { place, count }
      );
    }
    const lines = partial.steps.map((step) =>
      digitLine(step, partial.multiplierDigit, words)
    );
    lines.push(
      t(words.rowResult, {
        value: partial.value.toString(),
      })
    );
    return { heading, note, lines };
  });
}

/** The closing sentences: adding the rows, placing the point, applying the sign. */
export function closingLines(
  m: LongMultiplication,
  words: MultiplicationWords
): string[] {
  const lines: string[] = [];
  if (m.zero) {
    return [words.zeroProduct];
  }
  if (m.partials.length === 1) {
    lines.push(words.onlyRow);
  } else {
    lines.push(
      t(words.addRows, {
        sum: m.partials.map((p) => p.value.toString()).join(" + "),
        total: m.productDigits,
      })
    );
  }
  if (m.scale > 0) {
    lines.push(
      t(words.pointRule, {
        scale: m.scale,
        digits: m.productDigits,
        result: placePoint(m.productDigits, m.scale),
      })
    );
  }
  if (m.negative) {
    lines.push(words.signRule);
  }
  return lines;
}

/** Words for the skip-zero line, used when the multiplier contains a 0 digit. */
export function hasSkippedZero(m: LongMultiplication): boolean {
  return m.multiplier.includes("0") && !m.zero;
}
